(() => {
  const FILE_LIMIT = 100 * 1024 * 1024;
  const TOTAL_FILE_LIMIT = 500 * 1024 * 1024;
  const MAX_FILES = 50;
  const ALLOWED_FILE = /\.(ai|eps|jpe?g|png|pdf|tiff?|svg)$/i;
  const MAX_UPLOAD_ATTEMPTS = 3;
  const root = document.querySelector('[data-spark-custom-payment]');
  if (!root || root.dataset.initialized === 'true') return;
  root.dataset.initialized = 'true';

  const form = root.querySelector('[data-custom-payment-form]');
  const amountInput = form?.elements?.amount;
  const summary = root.querySelector('[data-payment-summary]');
  const submit = root.querySelector('[data-payment-submit]');
  const status = root.querySelector('[data-payment-status]');
  const uploadToggle = root.querySelector('[data-payment-upload-toggle]');
  const uploadPanel = root.querySelector('[data-payment-upload-panel]');
  const fileInput = root.querySelector('[data-payment-files]');
  const fileList = root.querySelector('[data-payment-file-list]');
  const fileButton = root.querySelector('.spark-custom-payment__file-button');
  const apiBase = String(root.dataset.apiBase || '').replace(/\/+$/, '');
  const configuredMinimum = Number(root.dataset.minimum);
  const configuredMaximum = Number(root.dataset.maximum);
  const minimum = Number.isFinite(configuredMinimum) && configuredMinimum > 0 ? configuredMinimum : 1;
  const maximum = Number.isFinite(configuredMaximum) && configuredMaximum > minimum ? configuredMaximum : 25000;
  const currency = new Intl.NumberFormat('en-CA', {style: 'currency', currency: 'CAD'});
  const requestId = globalThis.crypto?.randomUUID?.() || `payment-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  let queuedFiles = [];

  if (!form || !amountInput || !summary || !submit || !status || !apiBase) return;

  uploadToggle?.addEventListener('change', () => {
    const enabled = uploadToggle.checked;
    uploadPanel.hidden = !enabled;
    uploadToggle.setAttribute('aria-expanded', String(enabled));
    if (!enabled && fileInput) {
      queuedFiles = [];
      fileInput.value = '';
      renderFiles(queuedFiles);
    }
    status.textContent = '';
  });

  fileInput?.addEventListener('change', () => {
    status.textContent = '';
    const incomingFiles = [...(fileInput.files || [])];
    const nextFiles = mergeFiles(queuedFiles, incomingFiles);
    const error = validateFiles(nextFiles);
    fileInput.value = '';
    if (error) {
      status.textContent = error;
      return;
    }
    queuedFiles = nextFiles;
    renderFiles(queuedFiles);
  });

  const numericAmount = () => Number(String(amountInput.value || '').replace(/[$,\s]/g, ''));
  const renderAmount = () => {
    const amount = numericAmount();
    summary.textContent = Number.isFinite(amount) && amount > 0
      ? `${currency.format(amount)} CAD`
      : '$0.00 CAD';
  };

  amountInput.addEventListener('input', () => {
    status.textContent = '';
    amountInput.removeAttribute('aria-invalid');
    renderAmount();
  });

  amountInput.addEventListener('blur', () => {
    const amount = numericAmount();
    if (Number.isFinite(amount) && amount > 0) amountInput.value = amount.toFixed(2);
    renderAmount();
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    status.textContent = '';

    const amount = numericAmount();
    if (!Number.isFinite(amount) || amount < minimum || amount > maximum || !/^\d+(?:\.\d{1,2})?$/.test(String(amountInput.value).replace(/[$,\s]/g, ''))) {
      amountInput.setAttribute('aria-invalid', 'true');
      status.textContent = `Enter an amount between ${currency.format(minimum)} and ${currency.format(maximum)} CAD, using no more than two decimal places.`;
      amountInput.focus();
      return;
    }

    const files = selectedFiles();
    if (uploadToggle?.checked) {
      const uploadError = validateFiles(files, true);
      if (uploadError) {
        status.textContent = uploadError;
        fileInput?.focus();
        return;
      }
    }

    submit.disabled = true;
    root.setAttribute('aria-busy', 'true');
    submit.textContent = 'Preparing secure checkout...';

    try {
      const data = new FormData(form);
      let artworkManifest = '';
      if (uploadToggle?.checked) {
        submit.textContent = files.length === 1 ? 'Uploading file...' : `Uploading ${files.length} files...`;
        artworkManifest = await uploadFiles(files);
        submit.textContent = 'Preparing secure checkout...';
      }
      const response = await fetch(`${apiBase}/api/custom-payment/checkout`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({
          amount: amount.toFixed(2),
          reference: data.get('reference') || '',
          notes: data.get('notes') || '',
          discountCode: data.get('discountCode') || '',
          company: data.get('company') || '',
          requestId,
          artworkManifest,
          artworkFiles: files.map((file) => file.name),
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.checkoutUrl) {
        throw new Error(payload.error || 'We could not prepare your secure checkout. Please try again.');
      }
      const checkoutUrl = new URL(payload.checkoutUrl);
      if (checkoutUrl.protocol !== 'https:') throw new Error('Shopify returned an invalid checkout link.');
      window.location.assign(checkoutUrl.href);
    } catch (error) {
      status.textContent = error.message || 'We could not prepare your secure checkout. Please try again.';
      submit.disabled = false;
      root.removeAttribute('aria-busy');
      submit.textContent = 'Continue to secure checkout';
    }
  });

  function selectedFiles() {
    return uploadToggle?.checked ? [...queuedFiles] : [];
  }

  function mergeFiles(currentFiles, incomingFiles) {
    const merged = [...currentFiles];
    const knownFiles = new Set(currentFiles.map(fileIdentity));
    incomingFiles.forEach((file) => {
      const identity = fileIdentity(file);
      if (knownFiles.has(identity)) return;
      knownFiles.add(identity);
      merged.push(file);
    });
    return merged;
  }

  function fileIdentity(file) {
    return [file.name, file.size, file.type, file.lastModified].join(':');
  }

  function validateFiles(files, required = false) {
    if (required && files.length < 1) return 'Choose at least one file to upload.';
    if (files.length > MAX_FILES) return `Choose no more than ${MAX_FILES} files.`;
    const unsupported = files.find((file) => !ALLOWED_FILE.test(file.name));
    if (unsupported) return `${unsupported.name} uses an unsupported file type.`;
    const oversized = files.find((file) => file.size > FILE_LIMIT);
    if (oversized) return `${oversized.name} is too large. Maximum file size is 100 MB.`;
    const empty = files.find((file) => file.size < 1);
    if (empty) return `${empty.name} is empty.`;
    const total = files.reduce((sum, file) => sum + file.size, 0);
    if (total > TOTAL_FILE_LIMIT) return 'The combined upload is too large. Maximum combined size is 500 MB.';
    return '';
  }

  function renderFiles(files) {
    if (!fileList) return;
    fileList.replaceChildren(...files.map((file, index) => {
      const item = document.createElement('li');
      const details = document.createElement('span');
      details.className = 'spark-custom-payment__file-details';
      const name = document.createElement('span');
      const size = document.createElement('small');
      const remove = document.createElement('button');
      name.textContent = file.name;
      size.textContent = formatBytes(file.size);
      remove.type = 'button';
      remove.className = 'spark-custom-payment__file-remove';
      remove.dataset.paymentFileRemove = '';
      remove.textContent = '×';
      remove.setAttribute('aria-label', `Remove ${file.name}`);
      remove.addEventListener('click', () => {
        queuedFiles = queuedFiles.filter((_, queuedIndex) => queuedIndex !== index);
        renderFiles(queuedFiles);
        status.textContent = '';
        const remainingRemoveButtons = fileList.querySelectorAll('[data-payment-file-remove]');
        const nextButton = remainingRemoveButtons[Math.min(index, remainingRemoveButtons.length - 1)];
        (nextButton || fileButton)?.focus();
      });
      details.append(name, size);
      item.append(details, remove);
      return item;
    }));
  }

  function formatBytes(bytes) {
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  async function uploadFiles(files) {
    const authorizationResponse = await retryFetch(`${apiBase}/api/proofs/storefront-upload-authorize`, {
      method: 'POST',
      headers: {Accept: 'application/json', 'Content-Type': 'application/json'},
      body: JSON.stringify({
        productHandle: 'custom-payment',
        sessionId: uploadSessionId(),
        files: files.map((file) => ({
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          size: file.size,
        })),
      }),
    });
    const authorization = await authorizationResponse.json().catch(() => ({}));
    if (!authorizationResponse.ok || authorization.uploads?.length !== files.length) {
      throw new Error(authorization.error || 'Could not prepare the file upload.');
    }

    for (let index = 0; index < files.length; index += 1) {
      const file = files[index];
      const upload = authorization.uploads[index];
      submit.textContent = `Uploading ${index + 1} of ${files.length}...`;
      const uploadResponse = await retryFetch(upload.uploadUrl, {
        method: 'PUT',
        headers: {'Content-Type': upload.contentType || file.type || 'application/octet-stream'},
        body: file,
      });
      if (!uploadResponse.ok) throw new Error(`Could not upload ${file.name}. Please try again.`);
    }

    const completionResponse = await retryFetch(`${apiBase}/api/proofs/storefront-upload-complete`, {
      method: 'POST',
      headers: {Accept: 'application/json', 'Content-Type': 'application/json'},
      body: JSON.stringify({authorizationToken: authorization.authorizationToken}),
    });
    const completion = await completionResponse.json().catch(() => ({}));
    if (!completionResponse.ok || !completion.manifestToken) {
      throw new Error(completion.error || 'Could not verify the uploaded files.');
    }
    return completion.manifestToken;
  }

  function uploadSessionId() {
    const key = 'spark-custom-payment-upload-session';
    const stored = sessionStorage.getItem(key);
    if (stored) return stored;
    const value = `payment_${requestId.replace(/[^a-z0-9]/gi, '')}`;
    sessionStorage.setItem(key, value);
    return value;
  }

  async function retryFetch(url, options) {
    let lastError;
    for (let attempt = 1; attempt <= MAX_UPLOAD_ATTEMPTS; attempt += 1) {
      try {
        const response = await fetch(url, options);
        if (!retryableStatus(response.status) || attempt === MAX_UPLOAD_ATTEMPTS) return response;
        lastError = new Error(`Upload service returned HTTP ${response.status}.`);
      } catch (error) {
        lastError = error;
        if (attempt === MAX_UPLOAD_ATTEMPTS) throw error;
      }
      await new Promise((resolve) => window.setTimeout(resolve, 400 * (2 ** (attempt - 1))));
    }
    throw lastError || new Error('The upload service could not be reached.');
  }

  function retryableStatus(statusCode) {
    return [408, 425, 429].includes(Number(statusCode)) || Number(statusCode) >= 500;
  }
})();
